import { Vivid } from "@vendrinc/frontend-ts";
import * as React from "react";
import { useEventListener, useInterval, useTimeout } from "usehooks-ts";
import { NotificationsContext } from "~/contexts/notificationsContext";

export function useNotifications({
  isLoggedIn,
}: {
  isLoggedIn: boolean;
}): NotificationsContext {
  const watchingPriceCheckIds = React.useRef<Set<string> | null>(null);

  useTimeout(
    async () => {
      if (!isLoggedIn) {
        return;
      }

      const { ids } = await fetch("/api/price-checks/Preparing").then((r) =>
        r.json(),
      );

      if (ids.length > 0) {
        watchingPriceCheckIds.current = new Set(ids);
      }
    },
    isLoggedIn ? 300 : null,
  );

  async function checkStatus() {
    if (!isLoggedIn) {
      watchingPriceCheckIds.current = null;
      return;
    }

    if (!watchingPriceCheckIds.current?.size) {
      return;
    }

    const url = new URL("/api/price-checks/state", globalThis.location.origin);

    for (const id of watchingPriceCheckIds.current) {
      url.searchParams.append("id", id);
    }

    const { result } = await fetch(url).then((r) => r.json());

    for (const { id, state } of result) {
      const detailsPath = `/requests/${id}`;

      switch (state) {
        case "Delivered": {
          if (!globalThis.location.pathname.startsWith(detailsPath)) {
            // Show toast only if not already in price check details page
            Vivid.Snackbar.successToast({
              message: "Your price check is ready!",
              link: {
                label: "Review it now",
                href: `${detailsPath}/check`,
              },
            });
          }

          watchingPriceCheckIds.current.delete(id);
          break;
        }
        case "Undeliverable": {
          if (!globalThis.location.pathname.startsWith(detailsPath)) {
            // Show toast only if not already in price check details page
            Vivid.Snackbar.errorToast({
              message: "We couldn't deliver your price check",
              link: {
                label: "View",
                href: detailsPath,
              },
            });
          }

          watchingPriceCheckIds.current.delete(id);
          break;
        }
        case "Preparing":
          break;
        default:
          break;
      }
    }
  }

  useInterval(checkStatus, 10 * 1000);

  const documentRef = React.useRef<Document | null>(
    globalThis.document ?? null,
  );

  useEventListener(
    "visibilitychange",
    async () => {
      if (!globalThis.document?.hidden) {
        await checkStatus();
      }
    },
    documentRef,
  );

  return {
    watchPriceCheckStatus(id) {
      if (!watchingPriceCheckIds.current) {
        watchingPriceCheckIds.current = new Set();
      }

      watchingPriceCheckIds.current.add(id);
    },
  };
}
